import { all, call, put, select, take, takeLatest } from 'redux-saga/effects';

import * as actions from './actions';
import { reportAction } from '@api/activityLogApi';
import { NotificationType, TaskType } from 'shovel-lib/types';
import { openManageTaskDialog } from '../task/actions';
import { initFcmMessaging, isFcmMessagingSupported } from '../../config/firebaseInitializer';
import { notify } from '@components/common/snackbarNotifications/Notifications';
import { Dispatch } from 'redux';
import { getType } from 'typesafe-actions';
import * as semesterSelectors from '../semester/selectors';
import { push } from 'connected-react-router';
import { goToHomeLink } from '@components/navigation/navigationHelpers';
import { openSessionPopup } from '@utils/calendar/openSession';
import { showSessionNotice, toSessionPing } from '@utils/sessionNotice';
import { otherTabInView, shovelInView } from '@utils/timerAlertTabs';

// The ping's session, on the calendar, where the student logs it or starts its timer. Its task when the calendar can't
// show it: the session's gone, or it's on month view.
export function* openNotification(action: ReturnType<typeof actions.openNotification>) {
  const { type, payload } = action.payload;
  if (type !== NotificationType.PLANNED_TASK_STARTS && type !== NotificationType.PLANNED_TASK_ENDS) return;
  const semesterId = yield select(semesterSelectors.getId);
  if (!semesterId) return;
  yield put(push(goToHomeLink(semesterId)));
  const opened = yield call(openSessionPopup, Number(payload.plannedTaskId));
  if (!opened) yield put(openManageTaskDialog({ taskId: Number(payload.taskId), type: payload.taskType as TaskType }));
}

export const pingKind = (type: string) => (type === NotificationType.PLANNED_TASK_STARTS ? 'starts' : 'ended');

/**
 * A ping reaching an open tab: a toast if the student is looking at Shovel. If not, a desktop notice, as they'd never see
 * the toast; Firebase's worker shows that itself only with every Shovel tab hidden. One tab acts, so one toast.
 */
export const showPing = async (payload: any, dispatch: Dispatch) => {
  const { title, body } = payload.notification;
  const ping = toSessionPing(payload.data);
  if (!shovelInView()) {
    if (await otherTabInView()) return;
    if (await showSessionNotice(title, body, payload.data)) return;
  }
  notify({
    title,
    body,
    onClick: () => {
      reportAction('session_ping_clicked', { kind: pingKind(ping.type), from: 'toast' });
      dispatch(actions.openNotification(ping));
    }
  });
};

export function* watchForNotifications(dispatch: Dispatch) {
  if (!isFcmMessagingSupported) return;

  let listening = false;
  while (true) {
    yield take(getType(actions.startNotificationsListener));
    // Never asked here (timer-alerts spec, Petr 2026-10-31): getToken() raised the browser's prompt at every app start,
    // before the student knew what for. It's asked in context instead (the timer's "Get a ping when sessions start and
    // end?", Settings > Notifications), which starts this again once allowed. Started once, so messages aren't shown
    // twice.
    if (listening || typeof Notification === 'undefined' || Notification.permission !== 'granted') continue;
    listening = true;

    const messaging = yield call(initFcmMessaging);

    messaging.onMessage(payload => {
      showPing(payload, dispatch);
    });
  }
}

export default function*(dispatch: Dispatch) {
  yield all([watchForNotifications(dispatch), takeLatest(actions.openNotification, openNotification)]);
}
